import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from './service/auth.service';
import { JwtHelperService } from './service/jwt-helper.service';
import { TokenModel } from './model/token.model';
import * as AuthActions from './store/auth/actions/auth.actions';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { routes } from './app.routes';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { metaReducers } from './store/meta-reducer';
import { UserEffects } from './store/user/effects/user.effects';
import { provideEffects } from '@ngrx/effects';
import * as fromApp from './store/app.reducer';
import { AuthEffects } from './store/auth/effects/auth.effects';
import { ErrorEffects } from './store/error/effects/error.effects';
import { GdprEffects } from './store/gdpr/effects/gdpr.effects';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';

import { provideIonicAngular } from '@ionic/angular/standalone';
import { Space } from '../primeNG.presets';
import { authIntercept } from './service/auth-interceptor.service';
import { tokenRefreshIntercept } from './service/token-refresh.interceptor';
import { deviceFingerprintIntercept } from './service/device-fingerprint.interceptor';
import { GlobalErrorHandler } from './service/global-error-handler';
import { provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';

export const appConfig: ApplicationConfig = {
  providers: [
    // Global error handler for uncaught errors
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // SECURITY / UX: the access token is memory-only, so on a cold load / reload there is no token.
    // Block bootstrap on a single silent refresh against the HttpOnly cookie: if the cookie is valid
    // the session is restored before any route renders (no /login flash, and no protected route can
    // activate before auth is resolved); otherwise the user is sent to login. The refresh is bounded
    // by the 8s timeout in AuthService so a stalled network cannot hang startup forever. (There is
    // deliberately NO client-side cross-tab lock here — an earlier Web Locks attempt put an unbounded
    // lock-wait inside this blocking initializer and deadlocked startup; cross-tab refresh collisions
    // are handled on the server by the rotation grace window instead.)
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const store = inject(Store);
      const jwtHelper = inject(JwtHelperService);
      return firstValueFrom(
        authService.refreshAccessToken().pipe(
          tap((response) => {
            const token = new TokenModel();
            token.accessToken = response.accessToken;
            token.tokenType = 'Bearer';
            token.expiresIn = response.expiresIn;
            token.expiresAt = Date.now() + response.expiresIn * 1000;
            token.roles = jwtHelper.getRoles(response.accessToken);
            token.username = jwtHelper.getUsername(response.accessToken) || '';
            store.dispatch(AuthActions.checkAuthSuccess({ token }));
          }),
          catchError(() => {
            // No valid refresh cookie → unauthenticated. The checkAuthFailure effect redirects to /login.
            store.dispatch(AuthActions.checkAuthFailure());
            return of(null);
          }),
        ),
      );
    }),
    // Order matters: deviceFingerprint → tokenRefresh → auth
    // 1. Device headers must be present for login and token refresh requests
    // 2. Token refresh must run before auth to ensure a fresh token
    // 3. Auth interceptor adds the Authorization header last
    provideHttpClient(
      withInterceptors([deviceFingerprintIntercept, tokenRefreshIntercept, authIntercept]),
    ),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true, // GitHub Flavored Markdown
          breaks: true, // Convert line breaks to <br>
          pedantic: false,
        },
      },
    }),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
    provideStore(fromApp.appReducer, { metaReducers }),
    provideEffects([AuthEffects, UserEffects, ErrorEffects, GdprEffects]),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, // If true, the action's stack trace is included in the state log.
      traceLimit: 75, // The max number of stack frames to retain in the log.
      connectInZone: true, // If true, connects the devtools to the NgZone.
    }),
    provideAnimationsAsync(),
    provideIonicAngular({
      mode: 'ios', // Force iOS mode
    }),
    providePrimeNG({
      theme: {
        preset: Space,
        options: {
          prefix: 'p',
          darkModeSelector: 'system',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
  ],
};
